import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { useEffect } from 'react';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryArticles = getArticlesByCategory(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">分类未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">请检查链接或返回首页浏览</p>
            <Link
              to="/"
              className="mt-4 inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 秘密教学漫画 | ${category.description}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={category.description}
        keywords={`秘密教学漫画,${category.name},秘密教学漫画${category.name},漫画${category.name}`}
        canonicalUrl={`https://www.mimijiaoxue-manhua.com/category/${slug}`}
        ogTitle={pageTitle}
        ogDescription={category.description}
        ogType="website"
        ogUrl={`https://www.mimijiaoxue-manhua.com/category/${slug}`}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-500 ml-13">{category.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-400">
                <i className="ri-file-list-3-line text-3xl"></i>
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-700">暂无内容</h3>
              <p className="mt-2 text-sm text-foreground-500">该分类下暂时没有文章，请稍后再来</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}